// frontend/src/context/roles.js
// -----------------------------------------------------------------------------
// Hiérarchie et utilitaires de rôles (ED > GA > SUP > PD) + helpers robustes.
// Changements clés :
//  - Normalisation de rôle avec gestion d’ALIAS (ex: "Executive Director" -> "ED").
//  - Sémantique stricte d’accès :
//      * allowedRoles absent/undefined  -> AUTORISER (fallback permissif)
//      * allowedRoles === [] (vide)      -> INTERDIRE TOUT LE MONDE
//      * allowedRoles contient le rôle   -> AUTORISER
//  - Fonctions utilitaires : normalizeRole, isRoleAllowed, roleRank, hasAtLeast
// -----------------------------------------------------------------------------
//
// Pourquoi ?
// Backend et frontend n’emploient pas toujours les mêmes libellés de rôles.
// Ex : backend: "Executive Director" ; frontend: "ED" attendu dans allowedRoles.
// Sans aliasing, on obtient des 403 "fantômes" malgré une authent authentique.
//
// Avec ce module :
//  - Tout rôle est d’abord converti en *code canonique* (ED/GA/SUP/PD) via ALIASES.
//  - Les comparaisons d’accès utilisent uniquement ces codes canoniques.
// -----------------------------------------------------------------------------

// 1) Codes canoniques
export const ROLE = Object.freeze({
  ED: "ED", // Executive Director
  GA: "GA", // Grants Admin (Manager)
  SUP: "SUP", // Supervisor
  PD: "PD", // Programme Director
});

// 2) Hiérarchie (du plus faible au plus élevé)
export const ROLE_ORDER = [ROLE.PD, ROLE.SUP, ROLE.GA, ROLE.ED];

// 3) Outil interne : transformer une chaîne en clé d’alias robuste
//    - trim, lowercase, espaces & tirets -> underscore
function keyify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s\-]+/g, "_");
}

// 4) Table d’alias : mappe les libellés "backend" vers nos codes canoniques
//    ⚠️ Ajoute ici toutes les variantes que ton backend peut renvoyer.
const ALIASES = new Map([
  // Executive Director
  ["executive_director", ROLE.ED],
  ["executive director", ROLE.ED],
  ["ed", ROLE.ED],
  ["admin", ROLE.ED],
  ["administrator", ROLE.ED],

  // Grants Admin (souvent assimilé à "manager")
  ["grants_admin", ROLE.GA],
  ["grants admin", ROLE.GA],
  ["ga", ROLE.GA],
  ["manager", ROLE.GA],

  // Supervisor
  ["supervisor", ROLE.SUP],
  ["sup", ROLE.SUP],

  // Programme / Program Director
  ["programme_director", ROLE.PD],
  ["program_director", ROLE.PD],
  ["programme director", ROLE.PD],
  ["program director", ROLE.PD],
  ["pd", ROLE.PD],
]);

// 5) Normalisation d’un rôle en code canonique
//    - Si un alias est connu -> retourne ED/GA/SUP/PD
//    - Sinon -> fallback en UPPERCASE (ex: "USER" restera "USER")
export function normalizeRole(value) {
  if (value == null) return "";
  const k = keyify(value);
  return ALIASES.get(k) || String(value).trim().toUpperCase();
}

// 6) Rang hiérarchique (pour comparaisons "au moins tel rôle")
export function roleRank(role) {
  const r = normalizeRole(role);
  const idx = ROLE_ORDER.indexOf(r);
  return idx === -1 ? -1 : idx;
}

/**
 * 7) Vérifie si le rôle `userRole` est autorisé par la liste `allowedRoles`.
 *
 * Règles:
 *  - allowedRoles === undefined/null -> autoriser (fallback permissif/documentaire)
 *  - allowedRoles === [] (vide)      -> interdire tout le monde
 *  - sinon, inclusion après normalisation par alias (case-insensitive robuste)
 *
 * Astuce (optionnel) :
 *  - Si tu veux un wildcard, tu peux mettre "*" dans allowedRoles et ajouter :
 *      if (set.has("*")) return true;
 */
export function isRoleAllowed(userRole, allowedRoles) {
  if (allowedRoles == null) return true; // ⚠️ undefined/null -> ALLOW (fallback)
  if (Array.isArray(allowedRoles) && allowedRoles.length === 0) return false; // [] -> DENY

  const userCanonical = normalizeRole(userRole);
  const set = new Set((allowedRoles || []).map(normalizeRole));
  // if (set.has("*")) return true; // décommente si tu veux supporter un wildcard
  return set.has(userCanonical);
}

/** 8) Vrai si userRole est au moins aussi “haut” que requiredRole (selon ROLE_ORDER). */
export function hasAtLeast(userRole, requiredRole) {
  return roleRank(userRole) >= roleRank(requiredRole);
}
